import { useReactFlow, type Rect } from '@xyflow/react';
import type { CSSProperties } from 'react';

import { Dropdown, IconButton, type DropdownMenu } from '@commenda/pathfinder';
import { Copy01Icon } from '@commenda/pathfinder/icons/Copy01Icon';
import { Download01Icon } from '@commenda/pathfinder/icons/Download01Icon';
import { File05Icon } from '@commenda/pathfinder/icons/File05Icon';
import { Image01Icon } from '@commenda/pathfinder/icons/Image01Icon';

import type { StructureChartAdapter } from '../adapters';
import { floatingControlStyle } from './floating-control-style';

interface StructureExportMenuProps {
  companyId: number;
  companyName?: string;
  contentBounds: Readonly<Rect>;
  exporter: Pick<StructureChartAdapter, 'exportPng' | 'exportPdf' | 'copyPng'>;
}

const exportControlStyle: CSSProperties = {
  ...floatingControlStyle,
  right: 16,
};

const reportExportError = (error: unknown) =>
  console.error('Failed to export the structure chart', error);

// Must render inside <ReactFlowProvider>. The exports frame everything the chart draws, not only the cards.
const StructureExportMenu = ({
  companyId,
  companyName,
  contentBounds,
  exporter,
}: StructureExportMenuProps) => {
  const rf = useReactFlow();
  const exportMenu: DropdownMenu = {
    groups: [
      {
        id: 'export',
        items: [
          {
            id: 'png',
            type: 'action',
            label: 'Export as PNG',
            leadingIcon: <Image01Icon />,
            onClick: () => {
              exporter
                .exportPng(rf, companyName, contentBounds)
                .catch(reportExportError);
            },
          },
          {
            id: 'pdf',
            type: 'action',
            label: 'Export as PDF',
            leadingIcon: <File05Icon />,
            onClick: () => {
              exporter
                .exportPdf(rf, companyId, companyName, contentBounds)
                .catch(reportExportError);
            },
          },
          {
            id: 'copy',
            type: 'action',
            label: 'Copy as PNG',
            leadingIcon: <Copy01Icon />,
            onClick: () => {
              exporter.copyPng(rf, contentBounds).catch(reportExportError);
            },
          },
        ],
      },
    ],
  };

  return (
    <div style={exportControlStyle}>
      <Dropdown
        menu={exportMenu}
        side="top"
        align="end"
        sideOffset={8}
        trigger={
          <IconButton label="Export" size="sm">
            <Download01Icon />
          </IconButton>
        }
      />
    </div>
  );
};

export { StructureExportMenu };
